import React, { useMemo } from 'react';
import { getRiskScoreDisplayFormula, getRiskScorePopoverTitleProps, RISK_SCORE_DOC_LINK, RiskScoreDisableTag } from 'utils/riskScoreUtil';
import _ from 'lodash';
import { Popover, Tooltip, Typography } from 'antd';
import Vocabulary, { getVocabulary } from './Vocabulary';
import { useGlobalSettingsContext } from 'contexts/GlobalSettingsContext';
import { useFeatureFlag } from 'contexts/FeatureFlagContext';
import { withErrorBoundary } from 'react-error-boundary';
import ACRiskScoreTag from 'ac-components/ac-riskscore-tag/ACRiskScoreTag';
const { Link: HyperLink } = Typography;

function SubproductRiskScoreTag({ riskScoreInverted = false, data, v2Design }) {
  const { errorTools, inactiveTools, notConfiguredTools, severity, riskyFinding, unusedRecommendedTools, riskCalculationType } =
  data || {};

  const {riskScoreEnabled, riskScoreRange, maxRiskScore} = useGlobalSettingsContext();
  const featureFlags = useFeatureFlag();

  const _severity = useMemo(() => {
    if (!_.isNil(severity)) {
      const {
        Critical: critical,
        High: high,
        Medium: medium,
        Low: low,
      } = severity || {};
      if (critical) {
        return critical + ' Critical findings';
      } else if (high) {
        return high + ' High findings';
      } else if (medium) {
        return medium + ' Medium findings';
      } else if (low) {
        return low + ' Low findings';
      } else {
        return null;
      }
    } else {
      return null;
    }
  }, [severity]);

  const popoverTitleProps = getRiskScorePopoverTitleProps(data.score, riskScoreInverted, riskScoreRange, featureFlags.RISK_SCORE_3_0);
  const unusedToolCount = unusedRecommendedTools?.length ?? 0;
  const subproductMeanRiskCalculationTooltip = riskCalculationType && riskCalculationType === 'AVERAGE' ? <>Average <Vocabulary name="riskScore"/> of all findings under this <Vocabulary name="subProduct"/></> : null;
  const showNewScoreReason = featureFlags.RISK_SCORE_3_0 && (!_.isEmpty(riskyFinding) || unusedToolCount || subproductMeanRiskCalculationTooltip);

  const isRiskScoreExcellent = featureFlags.RISK_SCORE_3_0 || riskScoreEnabled ? data.score === 0 : data.score === 100;
  const popoverTitle = `Possible reason(s) for the ${getVocabulary('riskScore')} could be:`;

  //even if the risk is low and not 100/100 or 0/100 in case of inverted risk score
  //need to show the reason along with the title
  const popoverContent = () => {

    if(isRiskScoreExcellent){
      return <Typography.Text strong className={`${popoverTitleProps.titleStyle}-text`}>{popoverTitleProps.title}</Typography.Text>;
    } else if(showNewScoreReason){
      return (
        <div>
          <Typography.Text
            strong
            className={`${popoverTitleProps.titleStyle}-text`}
          >
            {popoverTitleProps.title}
          </Typography.Text>
          {unusedToolCount ? 
            unusedRecommendedToolsContent(unusedRecommendedTools, unusedToolCount, maxRiskScore)
            : subproductMeanRiskCalculationTooltip ? <>
            <br/>
            {subproductMeanRiskCalculationTooltip}
            </> 
            : (
            <>
              {getRiskScoreDisplayFormula(riskyFinding)}
              <div>
                Riskiest finding is{' '}
                <HyperLink
                  target="_blank"
                  href={`#/findings/${riskyFinding.id}`}
                  onClick={e => e.stopPropagation()}
                >
                  {riskyFinding.id}
                </HyperLink>
              </div>
            </>
          )}
          <div className={'m-t-xs'}>
                 {featureFlags.RISK_SCORE_3_0 && <div className="p-r-xs">If <Vocabulary name="riskScore" /> or <Vocabulary name="findingScore" /> are modified,<br/> expect a delay in the updates. </div>}
                 <div className="text-right tag-tooltip-doc-link">{RISK_SCORE_DOC_LINK}</div>
          </div>
        </div>
      );
    } else {
      return <>
      <Typography.Text strong className={`${popoverTitleProps.titleStyle}-text`}>{popoverTitleProps.title}</Typography.Text>
      <br/>
      <Typography.Text strong>{popoverTitle}</Typography.Text>
      <ul className="p-0 m-l-m m-b-0">
        {!_.isEmpty(errorTools) && (
          <li>Tool status for {errorTools.join()} is error</li>
        )}
        {!_.isEmpty(inactiveTools) && (
          <li>Tool status for {inactiveTools.join()} is inactive</li>
        )}
        {recommendedToolsMessage(notConfiguredTools)}
        {!_.isNil(_severity) && <li>{_severity} exist within the <Vocabulary name="subProduct"/></li>}
      </ul>
    </>
    }
  }
  
  const ScoreTagWithPopover = 
      riskScoreEnabled ? (
       !_.isNil(data.score)
        ?
        (<Popover content={popoverContent} placement="topLeft" arrowPointAtCenter>
          <div>
            <ACRiskScoreTag
              value={data.score}
            />
          </div>
        </Popover>
      ) : (
        <Tooltip title={!featureFlags.RISK_SCORE_3_0 && _.isNil(data.score) ? 'No Tools Configured' : ''}>
          <span><ACRiskScoreTag value={data.score} /></span>
        </Tooltip>
      )
      ) : (
        <RiskScoreDisableTag v2Design={v2Design} />
      )

  return <>{ScoreTagWithPopover}</>;
}

export default withErrorBoundary(SubproductRiskScoreTag, {
  fallbackRender: () => 'Info not available',
});

export const recommendedToolsMessage = (notConfiguredTools) => {
  if(!_.isEmpty(notConfiguredTools)){
    if(notConfiguredTools.length > 1){
     return <li style={{maxWidth: '400px'}}>{notConfiguredTools.join()} are recommended SSDLC tools that have not been configured and are being taken into consideration as {notConfiguredTools.length} impacting the risk score.</li>
    }else{
      return <li style={{maxWidth: '400px'}}>{notConfiguredTools[0]}  is a recommended SSDLC tool that has not been configured and is being taken into consideration as 1 Critical finding, impacting the risk score.</li>
    }
  }else{
    return null;
  }
};

export const unusedRecommendedToolsContent = (unusedRecommendedTools, unusedToolCount, maxRiskScore) => {
  const normalizedMinScore = Math.round((800 / 1000) * maxRiskScore);

  return (
    <div style={{ maxWidth: 400 }}>
      <strong>{unusedRecommendedTools.join(', ')} </strong>
      {unusedToolCount > 1 ? 'are' : 'is a'} recommended SSDLC tool
      {unusedToolCount > 1 ? 's' : ''}, and set to impact the <Vocabulary name="riskScore" />, if the tool
      {unusedToolCount > 1 ? 's' : ''}{' '}
      {unusedToolCount > 1 ? 'are' : 'is'} in error, inactive, or non-configured state, it will result in minimum <Vocabulary name="riskScore" /> of <strong>{normalizedMinScore}</strong>.
    </div>
  );
};

